'use client'

import { createContext, Dispatch, Fragment, SetStateAction, useContext, useEffect, useState } from 'react'

import { useSearchParams } from 'next/navigation'
import { toast } from 'react-toastify'

import { feedTypeCard } from '@/redux/api/feed/feed.type'
import { PostComment } from '@/components/modals/PostComment'
import { ModalSharePost } from '@/components/modals/ModalSharePost'
import SiderQC from '@/components/layouts/SideBar/SiderQC'
import { TextareaPostProvider } from '@/app/(global)/post-cre/components/TextareaPostProvider'
import { ModalCreatePost } from '@/components/modals/ModalCreatePost'
import { useDeletePostMutation } from '@/redux/api/post-cre/post.w3'

import styles from './styles.module.css'
import { ItemFeed } from './ItemFeed'

interface IFeedContext {
  datas: feedTypeCard[]
  setDatas: Dispatch<SetStateAction<feedTypeCard[]>>
}

export const FeedContext = createContext<IFeedContext | null>(null)

export const FeedProvider = ({ data, children }: { data: feedTypeCard[] | undefined; children?: React.ReactNode }) => {
  const [datas, setDatas] = useState<feedTypeCard[]>([])

  useEffect(() => {
    if (data) {
      setDatas(data)
    }
  }, [data])

  const values = {
    datas,
    setDatas,
  }
  if (!datas) return null
  return <FeedContext.Provider value={values}>{children}</FeedContext.Provider>
}

const CardSS: React.FC = () => {
  const data = useContext(FeedContext)?.datas
  const [isOpenCmt, setIsOpenCmt] = useState<boolean>(false)
  const [isOpenEditPost, setIsOpenEditPost] = useState<boolean>(false)
  const [isOpenSharePost, setIsOpenSharePost] = useState<boolean>(false)
  const [postId, setPostId] = useState('')
  const [post, setPost] = useState<feedTypeCard | null>(null)
  const searchParams = useSearchParams()
  const shareId = searchParams.get('shareID') as string
  const [deletePost] = useDeletePostMutation()
  const [postData, setPostData] = useState<feedTypeCard[]>([])

  useEffect(() => {
    if (data) {
      setPostData(data)
    }
  }, [data])

  useEffect(() => {
    if (shareId) {
      setIsOpenCmt(!isOpenCmt)
      setPostId(shareId)
    }
  }, [shareId])

  const handleOnComment = (id: string) => {
    if (!id) return
    setPostId(id)
    setIsOpenCmt(!isOpenCmt)
  }

  const handleOnUpdatePost = (_post: feedTypeCard) => {
    if (!_post) return
    setPost(_post)
    setIsOpenEditPost(!isOpenEditPost)
  }

  const handleDeletePost = async (id: string) => {
    try {
      const postRes = await deletePost(id).unwrap()
      if (postRes?.statusCode === 500) {
        if (postRes.message) {
          toast.error(postRes.message)
          return
        }
        throw new Error()
      }
      toast.success('Your post has been successfully deleted')
      setPostData((prev) => prev?.filter((it) => it.postId !== id))
    } catch (error) {}
  }

  const handleUpdateData = (updatedPost: any) => {
    const space = updatedPost.space
    const newData = {
      ...updatedPost,
      creatorId: space?.id || updatedPost.creator?.id,
      avatarImage: space?.profileImageUrl || updatedPost.creator?.avatarImage || '',
      priceBuyKey: updatedPost.creator?.priceBuyKey || 0,
      nickname: updatedPost.creator?.nickname || '',
      postId: updatedPost.id,
      role: updatedPost.creator?.role,
      postAs: space ? 'SPACE' : 'USER',
    }
    if (!updatedPost) {
      newData.content = ''
      setPost(newData)
    }
    setPostData((prev) => prev?.map((it) => (it.postId === updatedPost.id ? newData : it)))
  }

  if (!data) return null

  return (
    <Fragment>
      <div className={styles.cards__ss}>
        {postData.map((item: feedTypeCard, i: number) => {
          return (
            <Fragment key={i}>
              {i === 1 && (
                <div className="xl:hidden">
                  <SiderQC isMobile />
                </div>
              )}
              <ItemFeed key={i} item={item}>
                <ItemFeed.ItemFeedContent
                  onDelete={() => handleDeletePost(item.postId)}
                  onUpdatePost={() => handleOnUpdatePost(item)}
                  onComment={() => {
                    handleOnComment(item.postId)
                  }}
                >
                  <ItemFeed.FeedShare
                    onComment={() => {
                      handleOnComment(item?.originalPost?.postId)
                    }}
                  />
                  <ItemFeed.FeedAction
                    onComment={() => {
                      handleOnComment(item.postId)
                    }}
                    onSharePost={() => {
                      setPostId(item.postId)
                      setIsOpenSharePost(!isOpenSharePost)
                    }}
                    index={i}
                  />
                </ItemFeed.ItemFeedContent>
              </ItemFeed>
            </Fragment>
          )
        })}
      </div>

      <ModalSharePost
        postId={postId}
        isOpen={isOpenSharePost}
        toggleOpen={() => setIsOpenSharePost(!isOpenSharePost)}
      />
      <PostComment postId={postId} isOpen={isOpenCmt} onOpenChange={() => setIsOpenCmt(!isOpenCmt)} />
      <TextareaPostProvider post={post || undefined}>
        <ModalCreatePost
          isOpen={isOpenEditPost}
          isEdit
          onTonggle={(v: any) => {
            handleUpdateData(v)
            setIsOpenEditPost(!isOpenEditPost)
          }}
        />
      </TextareaPostProvider>
    </Fragment>
  )
}

export default CardSS
